import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button, 
  Chip, 
  Tooltip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Divider,
  TableHead,
  Checkbox,
  Stack,
  CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import StyledDataGrid from 'components/atoms/StyledDataGrid';
import { 
  fetchAdminOrders, 
  fetchOrderCopyOrders,
  selectAllAdminOrders, 
  setOrderFilter, 
  clearOrderFilter, 
  selectOrderFilter,
  selectCopyOrders,
  selectCopyOrdersLoading
} from 'redux/admin/adminOrderSlice';
import { formatDateTime } from 'utils/dateTime';
import CopyOrdersModal from 'components/molecules/CopyOrdersModal';

/**
 * ExpandableRow Component
 * A custom component for rendering expandable rows
 */
const ExpandableRow = ({ row, orders, filter, onCopyOrdersClick }) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  // Custom render the detail content
  const renderDetailContent = () => {
    return (
      <Box sx={{ px: 4, py: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Order Details</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Strategy ID</TableCell>
                  <TableCell>{row.strategy?.id || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Trade ID</TableCell>
                  <TableCell>
                    {row.trade ? `${row.trade.id}` : 'N/A'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>User</TableCell>
                  <TableCell>{row.user?.username || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Active</TableCell>
                  <TableCell>{row.active ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell>{row.data?.order_type || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Price</TableCell>
                  <TableCell>
                    {row.data?.price || row.data?.limit_price || row.data?.stop_price || 'N/A'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Parent Order</TableCell>
                  <TableCell>{row.parent_order_id || 'None'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Source Order</TableCell>
                  <TableCell>{row.source_order_id || 'None'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          
          {row.data && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Raw Data</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>Order Data</Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      borderRadius: 1,
                      overflowX: 'auto',
                      fontSize: '1rem',
                      height: '300px',
                      maxHeight: '300px'
                    }}
                  >
                    {JSON.stringify(row.data, null, 2)}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>Response</Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      borderRadius: 1,
                      overflowX: 'auto',
                      fontSize: '1rem',
                      height: '300px',
                      maxHeight: '300px'
                    }}
                  >
                    {JSON.stringify(row.response || {}, null, 2)}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>Order Status</Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      borderRadius: 1,
                      overflowX: 'auto',
                      fontSize: '1rem',
                      height: '300px',
                      maxHeight: '300px'
                    }}
                  >
                    {JSON.stringify({
                      order_status: row.order_status,
                      order_status_updated_at: row.order_status_updated_at
                    }, null, 2)}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }} size="small">
        <TableBody>
          <TableRow 
            sx={{ 
              display: 'flex', 
              width: '100%',
              alignItems: 'center',
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              },
              ...(row.has_copy_orders && 
                  row.copy_orders_sync_status &&
                  row.copy_orders_sync_status.synced < row.copy_orders_sync_status.total && {
                borderLeft: '4px solid',
                borderLeftColor: 'warning.main',
              })
            }}
          >
            <TableCell padding="checkbox" sx={{ width: '50px', borderBottom: 'none', flexShrink: 0 }}>
              <IconButton size="small" onClick={handleExpand}>
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </TableCell>
            <TableCell sx={{ width: '160px', borderBottom: 'none', flexShrink: 0 }}>
              {row.id}
              {row.parent_order_id && (
                <Tooltip title="Child Order">
                  <Chip 
                    size="small" 
                    label="Child" 
                    variant="outlined" 
                    color="info" 
                    sx={{ ml: 1, height: '16px', fontSize: '0.6rem' }} 
                  />
                </Tooltip>
              )}
            </TableCell>
            <TableCell sx={{ width: '180px', borderBottom: 'none', flexShrink: 0 }}>{row.strategy?.name || 'N/A'}</TableCell>
            <TableCell sx={{ width: '250px', borderBottom: 'none', flexShrink: 0 }}>{row.user?.username || 'N/A'}</TableCell>
            <TableCell sx={{ width: '150px', borderBottom: 'none', flexShrink: 0 }}>{formatDateTime(row.created)}</TableCell>
            <TableCell sx={{ width: '90px', borderBottom: 'none', flexShrink: 0 }}>{row.data?.symbol || 'N/A'}</TableCell>
            <TableCell sx={{ width: '80px', borderBottom: 'none', flexShrink: 0 }}>{row.data?.side || 'N/A'}</TableCell>
            <TableCell sx={{ width: '90px', borderBottom: 'none', flexShrink: 0 }}>{row.data?.quantity || 'N/A'}</TableCell>
            <TableCell sx={{ width: '100px', borderBottom: 'none', flexShrink: 0 }}>
              {(() => {
                const status = row.order_status?.[0]?.order_status || 'N/A';
                let color = 'text.primary';
                
                if (status === 'Filled') color = 'success.dark';
                else if (status === 'Submitted') color = 'info.main';
                else if (status === 'Cancelled') color = 'error.main';
                else if (status === 'PendingCancel') color = 'warning.main';
                
                return (
                  <Typography variant="body2" color={color}>
                    {status}
                  </Typography>
                );
              })()}
            </TableCell>
            <TableCell sx={{ width: '80px', borderBottom: 'none', flexShrink: 0 }}>
              {(() => {
                const active = row.active ? 'Yes' : 'No';
                let color = 'text.primary';
                
                if (active === 'Yes') color = 'success.dark';
                else if (active === 'No') color = 'text.secondary';
                
                return (
                  <Typography variant="body2" color={color}>
                    {active}
                  </Typography>
                );
              })()}
            </TableCell>
            <TableCell sx={{ width: '120px', borderBottom: 'none', flexShrink: 0 }}>
              {row.has_copy_orders && (
                <Tooltip title="View copy orders">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyOrdersClick(row.id);
                    }}
                  >
                    <ContentCopyIcon fontSize="10px" />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
            <TableCell sx={{ width: '120px', borderBottom: 'none', flexShrink: 0, flex: 1 }}>
              {row.has_copy_orders && row.copy_orders_sync_status && (() => {
                const { synced, total } = row.copy_orders_sync_status;
                const allSynced = synced === total;
                
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: allSynced ? 'success.dark' : 'warning.main',
                        fontWeight: allSynced ? 'normal' : 'bold'
                      }}
                    >
                      {synced}/{total} synced
                    </Typography>
                    
                  </Box>
                );
              })()}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {renderDetailContent()}
      </Collapse>
    </Box>
  );
};

/**
 * OrdersPage Component
 * Admin page for reviewing orders
 */
export default function OrdersPage() {
  const dispatch = useDispatch();
  const orders = useSelector(selectAllAdminOrders);
  const filter = useSelector(selectOrderFilter);
  const loading = useSelector(state => state.adminOrder.loading);
  const copyOrdersLoading = useSelector(selectCopyOrdersLoading);
  
  // State for modal
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showCopyOrdersModal, setShowCopyOrdersModal] = useState(false);
  
  // Get selected order and its copy orders
  const selectedOrder = useSelector(state => 
    selectedOrderId ? state.adminOrder.entities[selectedOrderId] : null
  );
  const copyOrders = useSelector(state => 
    selectedOrderId ? selectCopyOrders(state, selectedOrderId) : []
  );
  
  // Local filter state (for form controls)
  const [localFilter, setLocalFilter] = useState({
    orderId: '',
    strategyId: '',
    tradeId: '',
    date: '',
    orderStatus: '',
    active: ''
  });

  // Fetch orders on mount with current filter
  useEffect(() => {
    // Convert filter to query params
    const params = {};
    if (filter.orderId) params.id = filter.orderId;
    if (filter.strategyId) params.strategy_id = filter.strategyId;
    if (filter.tradeId) params.trade_id = filter.tradeId;
    if (filter.date) params.date = filter.date;
    if (filter.orderStatus) params.order_status = filter.orderStatus;
    if (filter.active !== null) params.active = filter.active;
    
    dispatch(fetchAdminOrders(params));
  }, [dispatch, filter]);

  // Apply filters
  const handleApplyFilters = () => {
    dispatch(setOrderFilter({
      orderId: localFilter.orderId || null,
      strategyId: localFilter.strategyId || null,
      tradeId: localFilter.tradeId || null,
      date: localFilter.date || null,
      orderStatus: localFilter.orderStatus || null,
      active: localFilter.active === '' ? null : localFilter.active === 'true'
    }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setLocalFilter({
      orderId: '',
      strategyId: '',
      tradeId: '',
      date: '',
      orderStatus: '',
      active: ''
    });
    dispatch(clearOrderFilter());
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setLocalFilter(prev => ({ ...prev, [name]: value }));
  };

  // Handle showing copy orders modal
  const handleShowCopyOrders = (orderId) => {
    setSelectedOrderId(orderId);
    dispatch(fetchOrderCopyOrders(orderId));
    setShowCopyOrdersModal(true);
  };

  // Handle closing copy orders modal
  const handleCloseCopyOrdersModal = () => {
    setShowCopyOrdersModal(false);
  };

  // Column headers for the custom table
  const columnHeaders = [
    { id: 'checkbox', label: '', width: '20px' },
    { id: 'id', label: 'ID', width: '160px' },
    { id: 'strategy', label: 'Strategy', width: '180px' },
    { id: 'user', label: 'User', width: '250px' },
    { id: 'date', label: 'Date', width: '150px' },
    { id: 'symbol', label: 'Symbol', width: '90px' },
    { id: 'side', label: 'Side', width: '80px' },
    { id: 'quantity', label: 'Quantity', width: '90px' },
    { id: 'status', label: 'Status', width: '100px' },
    { id: 'active', label: 'Active', width: '80px' },
    { id: 'copyOrders', label: 'Copy Orders', width: '120px' },
    { id: 'syncStatus', label: 'Sync Status', width: 'auto', flex: 1 },
  ];

  // Add helper functions for status colors
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'on-hold':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getFulfillmentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'fulfilled':
        return 'success';
      case 'partial':
        return 'warning';
      case 'unfulfilled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSyncStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'synced':
        return 'success.dark';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>Order Management</Typography>
      
      {/* Filter Controls */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 3,
          width: '100%'
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter Orders</Typography>
        <Grid container spacing={2}>
          {/* First row - ID filters */}
          <Grid item xs={12} sm={6} md={4}>
            <Tooltip title="Searching by Order ID will include both parent and child orders if they exist">
              <TextField
                fullWidth
                label="Order ID"
                name="orderId"
                value={localFilter.orderId}
                onChange={handleFilterChange}
                size="small"
                placeholder="Includes parent/child orders"
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Strategy ID"
              name="strategyId"
              value={localFilter.strategyId}
              onChange={handleFilterChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Trade ID"
              name="tradeId"
              value={localFilter.tradeId}
              onChange={handleFilterChange}
              size="small"
            />
          </Grid>
          
          {/* Second row - Date and dropdown filters + action buttons */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Date"
              name="date"
              type="date"
              value={localFilter.date}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order Status</InputLabel>
              <Select
                label="Order Status"
                name="orderStatus"
                value={localFilter.orderStatus}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PreSubmitted">PreSubmitted</MenuItem>
                <MenuItem value="Submitted">Submitted</MenuItem>
                <MenuItem value="Filled">Filled</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
                <MenuItem value="PendingCancel">PendingCancel</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Active</InputLabel>
              <Select
                label="Active"
                name="active"
                value={localFilter.active}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
              <Button 
                variant="outlined" 
                onClick={handleResetFilters}
                size="small"
              >
                Reset
              </Button>
              <Button 
                variant="contained" 
                onClick={handleApplyFilters}
                size="small"
              >
                Apply
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ 
        width: '100%', 
        overflow: 'auto',
        border: '1px solid rgba(224, 224, 224, 1)',
        borderRadius: 1,
      }}>
        <Box sx={{ 
          minWidth: 1020, 
          width: '100%',
        }}>
          <Table sx={{ 
            width: '100%',
            tableLayout: 'fixed',
          }}>
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
              <TableRow sx={{ width: '100%' }}>
                {columnHeaders.map((column) => (
                  <TableCell 
                    key={column.id} 
                    sx={{ 
                      width: column.width,
                      ...(column.flex && { flex: column.flex }),
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 'bold',
                      backgroundColor: (theme) => theme.palette.grey[100],
                      py: 0.5,
                      ...(column.id === 'copyOrders' && { textAlign: 'right' }),
                      ...(column.id === 'syncStatus' && { textAlign: 'right' })
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>
          
          {/* Loading indicator */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>Loading orders...</Typography>
            </Box>
          ) : (
            /* Order rows */
            <Box sx={{ width: '100%' }}>
              {orders.map(order => (
                <ExpandableRow 
                  key={order.id} 
                  row={order} 
                  orders={orders}
                  filter={filter}
                  onCopyOrdersClick={handleShowCopyOrders}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Copy Orders Modal */}
      <CopyOrdersModal
        open={showCopyOrdersModal}
        onClose={handleCloseCopyOrdersModal}
        mainOrder={selectedOrder}
        copyOrders={copyOrders}
        loading={copyOrdersLoading}
      />
    </Paper>
  );
} 